import { Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FocusTrap } from "focus-trap-react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  ArrowLeftRight,
  History as HistoryIcon,
  Key,
  LogOut,
  Menu as MenuIcon,
  Moon,
  ScanSearch,
  Sparkles,
  Sun,
  Tag,
  TrendingUp,
  User as UserIcon,
  UserCog,
  Users,
  Wand2,
  X,
} from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { LoadingScreen } from "./LoadingScreen";
import { useTheme } from "../theme/ThemeProvider";
import { Avatar } from "./ui/Avatar";
import { Badge } from "./ui/Badge";
import { cn } from "./ui/cn";

function OTGNavIcon({ className }: { className?: string }) {
  const { resolved } = useTheme();
  const icon = resolved === 'dark' ? '/faviconWhite.svg' : '/faviconBlack.svg';
  return <img src={icon} alt="OTG" className={className} />;
}

interface NavItemSpec {
  to: string;
  end?: boolean;
  label: string;
  icon: React.ReactNode;
  adminOnly?: boolean;
  managerOrAdminOnly?: boolean;
  requireMainGenerator?: boolean;
  requireTrafficGenerator?: boolean;
  requireRedirectManager?: boolean;
  section?: "main" | "tools" | "admin";
}

const NAV: NavItemSpec[] = [
  {
    to: "/",
    end: true,
    label: "Gerador",
    icon: <Wand2 className="size-4" />,
    requireMainGenerator: true,
    section: "main",
  },
  {
    to: "/traffic-generator",
    label: "Tráfego",
    icon: <TrendingUp className="size-4" />,
    requireTrafficGenerator: true,
    section: "main",
  },
  {
    to: "/redirect-manager",
    label: "Links Manychat",
    icon: <ArrowLeftRight className="size-4" />,
    requireRedirectManager: true,
    section: "main",
  },
  {
    to: "/history",
    label: "Histórico",
    icon: <HistoryIcon className="size-4" />,
    section: "tools",
  },
  {
    to: "/verificar-link",
    label: "Verificar Taxonomia",
    icon: <ScanSearch className="size-4" />,
    section: "tools",
  },
  {
    to: "/admin/entities",
    label: "Entidades",
    icon: <Tag className="size-4" />,
    adminOnly: true,
    section: "admin",
  },
  {
    to: "/admin/creators",
    label: "Creators",
    icon: <UserCog className="size-4" />,
    adminOnly: true,
    section: "admin",
  },
  {
    to: "/admin/users",
    label: "Usuários",
    icon: <Users className="size-4" />,
    managerOrAdminOnly: true,
    section: "admin",
  },
  {
    to: "/admin/api-keys",
    label: "Chaves de API",
    icon: <Key className="size-4" />,
    adminOnly: true,
    section: "admin",
  },
];

export function Layout() {
  const { user, permissions, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (!user) return <Outlet />;

  const isAdmin = user.role === "admin";
  const canAccessMain = isAdmin || (permissions?.generatorAccess?.canAccessMain ?? false);
  const canAccessTraffic = isAdmin || (permissions?.generatorAccess?.canAccessTraffic ?? false);
  const canAccessRedirectManager = isAdmin || (permissions?.generatorAccess?.canAccessRedirectManager ?? false);

  const navItems = NAV.filter(
    (n) =>
      (!n.adminOnly || isAdmin) &&
      (!n.managerOrAdminOnly || isAdmin || user.role === "manager") &&
      (!n.requireMainGenerator || canAccessMain) &&
      (!n.requireTrafficGenerator || canAccessTraffic) &&
      (!n.requireRedirectManager || canAccessRedirectManager),
  );
  const mainItems = navItems.filter((n) => n.section === "main");
  const toolsItems = navItems.filter((n) => n.section === "tools");
  const adminItems = navItems.filter((n) => n.section === "admin");

  return (
    <div className="min-h-screen flex bg-canvas">
      {/*
        ── Background decoration ──────────────────────────────────────
        z-0: painted above the body/canvas background-color (normal flow)
        but below the main content (z-[1]) and sidebar (z-40).
        pointer-events-none so it never blocks clicks.
      */}
      <div
        className="fixed inset-0 z-0 pointer-events-none overflow-hidden"
        aria-hidden
      >
        {/* Dots */}
        <div className="dot-grid absolute inset-0" />
      </div>

      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-canvas/80 md:hidden animate-fade-in"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/*
        Sidebar — desktop: fixed 240px
                — mobile: 280px drawer
        FocusTrap só ativa em mobile drawer aberto; em desktop o trap fica
        inativo e a sidebar funciona normal com hover-expand.
      */}
      <FocusTrap
        active={mobileOpen}
        focusTrapOptions={{
          escapeDeactivates: false,
          allowOutsideClick: true,
          fallbackFocus: '[data-mobile-drawer]',
        }}
      >
      <aside
        data-mobile-drawer
        tabIndex={-1}
        className={cn(
          "group/sidebar fixed top-0 left-0 z-40 h-screen h-dvh flex flex-col overflow-hidden outline-none",
          // Glassmorfismo: fundo semitransparente + blur intenso + borda sutil + sombra lateral
          "border-r border-line/40 bg-surface/50 backdrop-blur-md md:backdrop-blur-2xl",
          "shadow-[4px_0_32px_rgb(var(--shadow-color)/0.10)]",
          // Transição suave de largura (desktop) e translate (mobile) simultaneamente
          "transition-[width,transform] duration-300 ease-out",
          // Mobile: drawer 280px; desktop: colapsado 60px → expande para 240px no hover
          "w-[280px] md:w-[60px] md:hover:w-60",
          mobileOpen ? "translate-x-0 shadow-popover" : "-translate-x-full",
          "md:translate-x-0",
        )}
      >
        {/* Header */}
        <div className="flex items-center h-14 px-[14px] gap-3 border-b border-line shrink-0">
          <Link
            to="/"
            className="flex items-center gap-1.5 outline-none min-w-0"
            aria-label="OTG Tracker"
          >
            <OTGNavIcon className="size-8 shrink-0 object-contain" />
            {/* Texto do logo: invisível quando colapsado, aparece com fade ao expandir */}
            <span className="flex items-center gap-[0.2em] text-[18px] leading-none whitespace-nowrap translate-y-[2px] opacity-100 md:opacity-0 md:group-hover/sidebar:opacity-100 transition-opacity duration-200">
              <span className="font-sora font-extrabold tracking-tight text-ink leading-none">OTG</span>
              <span className="font-jetbrains font-normal text-ink-muted leading-none">tracker</span>
            </span>
          </Link>

          <button
            type="button"
            className="md:hidden ml-auto p-1.5 rounded-lg text-ink-muted hover:bg-surface-muted transition-colors"
            onClick={() => setMobileOpen(false)}
            aria-label="Fechar menu"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-5 overflow-y-auto overflow-x-hidden no-scrollbar">
          <NavGroup label="Principal">
            {mainItems.map((n) => (
              <SidebarItem key={n.to} item={n} />
            ))}
          </NavGroup>
          {toolsItems.length > 0 && (
            <NavGroup label="Ferramentas">
              {toolsItems.map((n) => (
                <SidebarItem key={n.to} item={n} />
              ))}
            </NavGroup>
          )}
          {adminItems.length > 0 && (
            <NavGroup label="Admin">
              {adminItems.map((n) => (
                <SidebarItem key={n.to} item={n} />
              ))}
            </NavGroup>
          )}
        </nav>

        {/* Bottom */}
        <div className="px-2 pt-3 pb-20 md:pb-6 border-t border-line space-y-1 shrink-0">
          <ThemeToggleItem />
          <UserBlock
            name={user.name}
            email={user.email}
            role={user.role}
            pictureUrl={user.pictureUrl}
            onProfile={() => navigate("/me")}
            onLogout={() => {
              void logout().then(() => navigate("/login"));
            }}
          />
        </div>
      </aside>
      </FocusTrap>

      {/* Main area — z-[1] keeps content above z-0 background orbs */}
      {/* md:ml-[60px] = largura da sidebar colapsada; sidebar expande por cima (overlay) no hover */}
      <div className="relative z-[1] flex-1 flex flex-col min-w-0 md:ml-[60px]">
        {/* Topbar */}
        <header
          className="sticky top-0 z-20 bg-surface/50 backdrop-blur-md md:backdrop-blur-2xl border-b border-line/40 shadow-[0_4px_32px_rgb(var(--shadow-color)/0.10)]"
          style={{
            paddingTop: "env(safe-area-inset-top, 0px)",
          }}
        >
          <div className="h-14 flex items-center gap-3 px-4 md:px-6">
            <button
              type="button"
              className="md:hidden p-2 -ml-1 rounded-lg text-ink-muted hover:bg-surface-muted transition-colors"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menu"
            >
              <MenuIcon className="size-5" />
            </button>

            <div className="flex-1 min-w-0" />

            <div className="flex items-center gap-2 shrink-0">
              {user.role === "admin" && (
                <Badge
                  tone="brand"
                  className="hidden sm:inline-flex"
                  leftIcon={<Sparkles className="size-3" />}
                >
                  Admin
                </Badge>
              )}
              {user.role === "manager" && (
                <Badge
                  tone="neutral"
                  className="hidden sm:inline-flex"
                >
                  Manager
                </Badge>
              )}
              {user.role === "viewer" && (
                <Badge tone="neutral" className="hidden sm:inline-flex">
                  Viewer
                </Badge>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main
          className="flex-1 px-4 sm:px-6 md:px-8 py-6 md:py-8 min-w-0"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 1.5rem)" }}
        >
          <div className="max-w-6xl mx-auto w-full">
            {/* As páginas são lazy (ver App.tsx). O boundary fica AQUI, dentro do
                <main>, pra que sidebar/topo permaneçam montados enquanto o chunk
                da rota nova baixa — um Suspense mais acima desmontaria a moldura
                inteira e a troca de rota piscaria. */}
            <Suspense fallback={<LoadingScreen fullscreen={false} />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

    </div>
  );
}

/* ── ThemeToggleItem ──────────────────────────────────────── */

function ThemeToggleItem() {
  const { resolved, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={resolved === 'dark' ? 'Mudar para claro' : 'Mudar para escuro'}
      className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-muted hover:text-ink hover:bg-surface-muted/70 border border-transparent transition-colors duration-150"
    >
      <span className="flex size-6 items-center justify-center rounded-md shrink-0 text-ink-subtle">
        {resolved === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </span>
      <span className="truncate opacity-100 md:opacity-0 md:group-hover/sidebar:opacity-100 transition-opacity duration-200">
        {resolved === 'dark' ? 'Modo claro' : 'Modo escuro'}
      </span>
    </button>
  );
}

/* ── NavGroup ──────────────────────────────────────────────── */

function NavGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      {label && (
        <div className="px-3 mb-1.5 text-[10px] uppercase tracking-widest font-semibold text-ink-subtle whitespace-nowrap opacity-100 md:opacity-0 md:group-hover/sidebar:opacity-100 transition-opacity duration-200">
          {label}
        </div>
      )}
      {children}
    </div>
  );
}

/* ── SidebarItem ──────────────────────────────────────────── */

function SidebarItem({ item }: { item: NavItemSpec }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={item.label}
      className={({ isActive }) =>
        cn(
          "relative flex items-center gap-3 rounded-lg text-sm font-medium transition-colors duration-150",
          "px-3 py-2",
          isActive
            ? "text-ink bg-surface-muted border border-line"
            : "text-ink-muted hover:text-ink hover:bg-surface-muted/70 border border-transparent",
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-r-full bg-brand-500" />
          )}

          <span
            className={cn(
              "flex size-6 items-center justify-center rounded-md shrink-0 transition-colors",
              isActive ? "text-brand-600" : "text-ink-subtle",
            )}
          >
            {item.icon}
          </span>

          {/* Label do item: sempre visível no drawer mobile;
              em desktop, oculto até o hover expandir a sidebar. */}
          <span className="truncate opacity-100 md:opacity-0 md:group-hover/sidebar:opacity-100 transition-opacity duration-200">
            {item.label}
          </span>
        </>
      )}
    </NavLink>
  );
}

/* ── UserBlock ────────────────────────────────────────────── */

function UserBlock({
  name,
  email,
  role,
  pictureUrl,
  onProfile,
  onLogout,
}: {
  name: string;
  email: string;
  role: "admin" | "user" | "manager" | "viewer";
  pictureUrl?: string | null;
  onProfile: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ bottom: 0, left: 0, width: 256 });
  const ref = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (
        ref.current && !ref.current.contains(target) &&
        popoverRef.current && !popoverRef.current.contains(target)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  function handleToggle() {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ bottom: window.innerHeight - r.top + 8, left: r.left, width: r.width });
    }
    setOpen((p) => !p);
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Menu da conta de ${name}`}
        className="w-full flex items-center gap-2.5 rounded-lg border border-transparent hover:border-line hover:bg-surface-muted transition-colors p-1.5"
      >
        {/* Avatar é decorativo neste contexto: nome e email já são texto adjacente
            (leitor de tela lê o aria-label do botão pai). */}
        <span aria-hidden="true" className="shrink-0">
          <Avatar name={name} pictureUrl={pictureUrl} size="sm" />
        </span>
        {/* Nome/email: sempre visíveis no drawer mobile;
            em desktop, fade-in quando a sidebar expande no hover. */}
        <div className="flex-1 min-w-0 text-left opacity-100 md:opacity-0 md:group-hover/sidebar:opacity-100 transition-opacity duration-200">
          <div className="text-sm font-medium text-ink truncate">{name}</div>
          <div className="text-[11px] text-ink-subtle truncate">{email}</div>
        </div>
      </button>

      {open && createPortal(
        <div
          ref={popoverRef}
          style={{ bottom: pos.bottom, left: pos.left, width: pos.width }}
          className="fixed z-[200] bg-surface-muted rounded-lg shadow-popover border border-line overflow-hidden animate-fade-in"
        >
          <div className="px-4 py-3 border-b border-line">
            <div className="text-sm font-semibold text-ink truncate">
              {name}
            </div>
            <div className="text-xs text-ink-muted truncate">{email}</div>
            <Badge
              tone={role === "admin" ? "brand" : role === "manager" ? "sky" : "neutral"}
              className="mt-2"
            >
              {role}
            </Badge>
          </div>
          <div className="p-1.5">
            <MenuItem
              icon={<UserIcon className="size-4" />}
              onClick={onProfile}
            >
              Meu perfil
            </MenuItem>
          </div>
          <div className="p-1.5 border-t border-line">
            <MenuItem
              icon={<LogOut className="size-4" />}
              onClick={onLogout}
              danger
            >
              Sair
            </MenuItem>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ── MenuItem ─────────────────────────────────────────────── */

function MenuItem({
  icon,
  onClick,
  children,
  danger,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm transition-colors",
        danger
          ? "text-danger hover:bg-danger-soft/60"
          : "text-ink-muted hover:text-ink hover:bg-surface-muted",
      )}
    >
      {icon}
      {children}
    </button>
  );
}
